import { FormattedMessage, useIntl } from '@alltrails/shared/react-intl';
import { useEffect } from 'react';
import Button from '@alltrails/shared/denali/components/Button';
import Typography from '@alltrails/shared/denali/components/Typography';
import logExploreWithProButtonClicked from '@alltrails/analytics/events/logExploreWithProButtonClicked';
import logProWelcomePageViewed from '@alltrails/analytics/events/logProWelcomePageViewed';
import CustomProvider from 'components/CustomProvider';
import { wrapUrlSafe } from 'utils/language_support_util';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import useUser from 'hooks/useUser';
import ApplicationLayout from 'components/ApplicationLayout';
import { Context } from '@alltrails/shared/types/context';
import PlusBadge from '@alltrails/shared/denali/components/PlusBadge';
import classNames from 'classnames';
import useIsMobile from '@alltrails/shared/hooks/useIsMobile';
import AppStore from '@alltrails/shared/components/storeBadges/AppStore';
import GooglePlay from '@alltrails/shared/components/storeBadges/GooglePlay';
import * as styles from './PlusWelcome.module.scss';

import plantsHikersJpg from './images/plants-hikers.jpg';
import plantsHikersWebp from './images/plants-hikers.webp';
import plantsHikersAvif from './images/plants-hikers.avif';
import portraitPhotoJpg from './images/portrait-photo.jpg';
import portraitPhotoWebp from './images/portrait-photo.webp';
import portraitPhotoAvif from './images/portrait-photo.avif';
import plusQRCodeJpg from './images/plus-welcome-qr.jpg';
import plusQRCodeWebp from './images/plus-welcome-qr.webp';
import plusQRCodeAvif from './images/plus-welcome-qr.avif';

const br = () => <br />;

// https://www.figma.com/file/LF38o2YcGyX1IXYdjWzhnM/Welcome-to-Plus?node-id=714%3A25514

function PlusWelcome() {
  const intl = useIntl();
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const isMobile = useIsMobile();

  useEffect(() => {
    logProWelcomePageViewed();
  }, []);

  const buttonDestination = wrapUrlSafe(user ? '/explore' : '/login', languageRegionCode);

  return (
    <CustomProvider>
      <div className={styles.content}>
        <div className={styles.welcome}>
          {/* Add mobile & desktop badges to prevent flicker */}
          <div className={styles.mobileBadge}>
            <PlusBadge size="lg" />
          </div>
          <div className={styles.desktopBadge}>
            <PlusBadge size="xl" />
          </div>

          <div className={classNames(styles.headingContainer, { [styles.mobile]: isMobile })}>
            <Typography component="h1" variant="heading700" className={styles.heading}>
              <FormattedMessage defaultMessage="Welcome to<br></br> AllTrails+" values={{ br }} />
            </Typography>
            <Typography variant="paragraph300" className={styles.subheading}>
              <FormattedMessage
                defaultMessage="You can now do even more outdoors with extra safety and planning<br></br> features like offline maps, 3D views, and wrong-turn alerts."
                values={{ br }}
              />
            </Typography>
          </div>
          <Button
            className={styles.btn}
            text={<FormattedMessage defaultMessage="Start exploring" />}
            linkInfo={{ href: buttonDestination }}
            onClick={() => logExploreWithProButtonClicked()}
            testId="pro-welcome-start-exploring"
            variant="primary"
            size="lg"
          />
          {isMobile && (
            <div className={styles.appStoreButtons}>
              <AppStore height={42} languageRegionCode={languageRegionCode} />
              <GooglePlay height={42} languageRegionCode={languageRegionCode} />
            </div>
          )}
          <div className={classNames(styles.bottomRowContainer, { [styles.mobile]: isMobile })}>
            <div className={styles.colContainer}>
              <picture className={styles.imgContainer}>
                <source srcSet={plantsHikersAvif} type="image/avif" />
                <source srcSet={plantsHikersWebp} type="image/webp" />
                <img
                  className={styles.sideImage}
                  src={plantsHikersJpg}
                  alt={intl.formatMessage({
                    defaultMessage: 'Hikers walking up stairs surrounded by plants.'
                  })}
                />
              </picture>
            </div>
            {!isMobile && (
              <div className={classNames(styles.colContainer, styles.qrCodeContainer)}>
                <picture>
                  <source srcSet={plusQRCodeAvif} type="image/avif" />
                  <source srcSet={plusQRCodeWebp} type="image/webp" />
                  <img src={plusQRCodeJpg} alt="" />
                </picture>

                <Typography variant="heading400" component="h2">
                  <FormattedMessage defaultMessage="Get the best of AllTrails+" />
                </Typography>
                <Typography variant="paragraph100" component="p">
                  <FormattedMessage defaultMessage="Scan the QR code to download<br></br> for iOS or Android" values={{ br }} />
                </Typography>
              </div>
            )}
            <div className={styles.colContainer}>
              <picture className={styles.imgContainer}>
                <source srcSet={portraitPhotoAvif} type="image/avif" />
                <source srcSet={portraitPhotoWebp} type="image/webp" />
                <img
                  className={styles.sideImage}
                  src={portraitPhotoJpg}
                  alt={intl.formatMessage({
                    defaultMessage: 'Close-up photo of a person smiling and wearing sunglasses.'
                  })}
                />
              </picture>
            </div>
          </div>
        </div>
      </div>
    </CustomProvider>
  );
}

type Props = {
  context: Context;
  currentPage: string;
  locationSlug: string;
};

export default function Wrapper({ context, currentPage, locationSlug }: Props) {
  return (
    <ApplicationLayout context={context} currentPage={currentPage} locationSlug={locationSlug}>
      <CustomProvider>
        <PlusWelcome />
      </CustomProvider>
    </ApplicationLayout>
  );
}
